import React, { createContext, useContext, useState, useRef, useMemo, useEffect } from "react";
import useToast from "@/hooks/useToast";
import type { EntityEditContextType, EntityEditProviderProps } from "@/types/contexts/entityEdit";

export const EntityEditContext = createContext<EntityEditContextType | null>(null);

export function EntityEditProvider<T>({
  children,
  entityData,
  loading = false,
  onSave,
  onSaveSuccess,
  entityName,
}: EntityEditProviderProps<T>) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<T>>({});
  const [metadata, setMetadata] = useState<Record<string, any>>({});
  const baseline = useRef<string>("");

  // Initialize formData when entityData changes
  useEffect(() => {
    if (entityData) {
      setFormData(entityData);
      baseline.current = JSON.stringify(entityData);
    }
  }, [entityData]);

  // Calculate dirty state
  const dirty = useMemo(() => {
    const hasFormChanges = JSON.stringify(formData) !== baseline.current;
    const hasMetadataChanges = Object.keys(metadata).length > 0;
    return hasFormChanges || hasMetadataChanges;
  }, [formData, metadata]);

  const updateFormData = (updates: Partial<T>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const updateMetadata = (updates: Record<string, any>) => {
    setMetadata((prev) => ({ ...prev, ...updates }));
  };

  const handleSave = async () => {
    if (!entityData || !dirty) return;

    setSaving(true);
    try {
      const updatedEntity = await onSave(formData, metadata);

      // Update both states with the response from the database
      setFormData(updatedEntity);
      baseline.current = JSON.stringify(updatedEntity);
      setEditing(false);
      setMetadata({}); // Clear metadata after successful save

      // Trigger refetch to update source entityData
      onSaveSuccess?.();

      toast.showToast({
        title: "Success",
        message: `${entityName} saved successfully`,
        toastType: "success",
      });
    } catch (error: any) {
      console.error(`Error saving ${entityName}:`, error);
      toast.showToast({
        title: "Error",
        message: error.message || `Failed to save ${entityName}`,
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (dirty) {
      // Reset form data to original entity data
      if (entityData) {
        setFormData(entityData);
      }
    }
    setMetadata({}); // Clear metadata on cancel
    setEditing(false);
  };

  const resetForm = () => {
    if (entityData) {
      setFormData(entityData);
      baseline.current = JSON.stringify(entityData);
    }
  };

  const contextValue: EntityEditContextType<T> = {
    entityData,
    formData,
    editing,
    dirty,
    loading,
    saving,
    metadata,
    setEditing,
    updateFormData,
    updateMetadata,
    handleSave,
    handleCancel,
    resetForm,
    onSaveSuccess,
  };

  return <EntityEditContext.Provider value={contextValue}>{children}</EntityEditContext.Provider>;
}

export function useEntityEdit<T = any>(): EntityEditContextType<T> {
  const context = useContext(EntityEditContext);
  if (!context) {
    throw new Error("useEntityEdit must be used within an EntityEditProvider");
  }
  return context as EntityEditContextType<T>;
}
